import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { Phone } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > previous && latest > 150) {
      setHidden(true);
    } else {
      setHidden(false);
    }
  });

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  // Animated hamburger menu button
  const MenuButton = () => (
    <motion.button
      data-testid="mobile-menu-toggle"
      className="md:hidden relative w-12 h-12 rounded-xl glass border border-gold/20 flex items-center justify-center"
      onClick={() => setIsOpen(!isOpen)}
      whileTap={{ scale: 0.95 }}
      whileHover={{ borderColor: 'rgba(212, 175, 55, 0.4)' }}
    >
      <div className="w-5 h-4 flex flex-col justify-between items-center">
        <motion.span
          className="w-full h-0.5 bg-gold rounded-full origin-left"
          animate={isOpen ? { rotate: 45, y: 0, width: '100%' } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        />
        <motion.span
          className="w-full h-0.5 bg-gold rounded-full"
          animate={isOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
        />
        <motion.span
          className="w-full h-0.5 bg-gold rounded-full origin-left"
          animate={isOpen ? { rotate: -45, y: 0, width: '100%' } : { rotate: 0, y: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        />
      </div>
    </motion.button>
  );

  return (
    <motion.nav
      data-testid="navbar"
      variants={{
        visible: { y: 0 },
        hidden: { y: "-100%" },
      }}
      animate={hidden ? "hidden" : "visible"}
      transition={{ duration: 0.35, ease: "easeInOut" }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* Desktop Navbar */}
      <div className="hidden md:block mx-4 lg:mx-8 mt-4">
        <div className="glass rounded-2xl border border-gold/10 px-6 py-4">
          <div className="max-w-[1400px] mx-auto flex items-center justify-between">
            {/* Logo - Far Left */}
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <motion.span
                className="logo-signature"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                Reagans
              </motion.span>
            </Link>

            {/* Navigation - Far Right */}
            <div className="flex items-center space-x-8">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                    className={`font-mono text-xs uppercase tracking-[0.2em] transition-all duration-300 relative group ${isActive(link.path)
                      ? 'text-gold'
                      : 'text-white/60 hover:text-gold'
                      }`}
                  >
                    {link.name}
                    <span className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                      }`} />
                  </Link>
                </motion.div>
              ))}

              {/* Call Button */}
              <motion.a
                href="tel:+27699512915"
                data-testid="nav-call-button"
                className="glass-button px-6 py-3 rounded-full font-mono text-xs uppercase tracking-widest text-gold flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Phone size={14} strokeWidth={1.5} />
                Call
              </motion.a>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navbar */}
      <div className="md:hidden mx-4 mt-4">
        <div className="glass rounded-2xl border border-gold/10 px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="nav-logo-mobile">
              <motion.span
                className="logo-signature text-2xl"
                whileHover={{ scale: 1.05 }}
              >
                Reagans
              </motion.span>
            </Link>

            {/* Animated Menu Button */}
            <MenuButton />
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="mt-3 glass rounded-2xl border border-gold/10 p-6 overflow-hidden"
            >
              <div className="space-y-4">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                      className={`block font-mono text-sm uppercase tracking-[0.15em] py-3 px-4 rounded-xl transition-all ${isActive(link.path)
                        ? 'text-gold bg-gold/10'
                        : 'text-white/60 hover:text-gold hover:bg-white/5'
                        }`}
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="pt-4 border-t border-white/10"
                >
                  <a
                    href="tel:+27699512915"
                    className="flex items-center justify-center gap-2 glass-button px-6 py-4 rounded-xl font-mono text-xs uppercase tracking-widest text-gold"
                  >
                    <Phone size={14} strokeWidth={1.5} />
                    Call Now
                  </a>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
